// src/pages/Warehouses/Reports/AggregatedReport.jsx
// تقرير حركة الأصناف (تجميعي) — الوارد والصادر والصافي وتكلفتها لكل صنف/فرع/مستودع

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

const qtyCell = (color) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 13.5, color: Number(p.value) === 0 ? "#cbd5e1" : color }}>
    {fmtNumber(p.value)}
  </Typography>
);

const moneyCell = (color) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 13.5, color: Number(p.value) === 0 ? "#cbd5e1" : color }}>
    {fmtMoney(p.value)}
  </Typography>
);

export default function AggregatedReport() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [product, setProduct] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [movement, setMovement] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || [])).catch(() => {});
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const productIdOf = (p) => p?.id ?? p?.productId;

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const pid = productIdOf(product);
      const params = {
        ...(pid && { productId: pid }),
        ...(branchId && { branchId }),
        ...(stockId && { stockId }),
        dateFrom: `${range.dateFrom}T00:00:00`,
        dateTo: `${range.dateTo}T23:59:59`,
      };
      const res = await axios.get(`/StockMovements/summary-report/${companyId}`, { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({
        id: i + 1,
        productName: r.productName,
        branchName: r.branchName,
        stockName: r.stockName,
        totalIn: Number(r.totalIn) || 0,
        totalOut: Number(r.totalOut) || 0,
        netMovement: Number(r.netMovement) || 0,
        costIn: Number(r.costIn) || 0,
        costOut: Number(r.costOut) || 0,
      })));
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, product, branchId, stockId, range.dateFrom, range.dateTo]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (q && !(r.productName ?? "").toLowerCase().includes(q)) return false;
      if (movement === "in" && r.totalIn <= 0) return false;
      if (movement === "out" && r.totalOut <= 0) return false;
      if (movement === "neg" && r.netMovement >= 0) return false;
      return true;
    });
  }, [rows, search, movement]);

  const totals = useMemo(() => visibleRows.reduce((a, r) => ({
    totalIn: a.totalIn + r.totalIn,
    totalOut: a.totalOut + r.totalOut,
    net: a.net + r.netMovement,
    costIn: a.costIn + r.costIn,
    costOut: a.costOut + r.costOut,
  }), { totalIn: 0, totalOut: 0, net: 0, costIn: 0, costOut: 0 }), [visibleRows]);

  const columns = useMemo(() => [
    {
      field: "productName", headerName: "الصنف", flex: 1.6, minWidth: 190, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || "—"}</Typography>,
    },
    { field: "branchName", headerName: "الفرع", flex: 1, minWidth: 120, headerAlign: "center", align: "center" },
    { field: "stockName", headerName: "المستودع", flex: 1, minWidth: 120, headerAlign: "center", align: "center" },
    { field: "totalIn", headerName: "الوارد", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: qtyCell("#047857") },
    { field: "totalOut", headerName: "الصادر", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: qtyCell("#be123c") },
    {
      field: "netMovement", headerName: "الصافي", type: "number", width: 110, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const v = Number(p.value) || 0;
        return (
          <Box sx={{
            px: 1.25, py: .3, borderRadius: "8px", fontWeight: 800, fontSize: 13.5, direction: "ltr",
            bgcolor: v > 0 ? "#d1fae5" : v < 0 ? "#ffe4e6" : "#f1f5f9",
            color: v > 0 ? "#047857" : v < 0 ? "#be123c" : "#94a3b8",
          }}>
            {v > 0 ? "+" : ""}{fmtNumber(v)}
          </Box>
        );
      },
    },
    { field: "costIn", headerName: "تكلفة الوارد", type: "number", width: 125, headerAlign: "center", align: "center", renderCell: moneyCell("#0f172a") },
    { field: "costOut", headerName: "تكلفة الصادر", type: "number", width: 125, headerAlign: "center", align: "center", renderCell: moneyCell("#0f172a") },
  ], []);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "الصنف": r.productName,
      "الفرع": r.branchName,
      "المستودع": r.stockName,
      "الوارد": r.totalIn,
      "الصادر": r.totalOut,
      "الصافي": r.netMovement,
      "تكلفة الوارد": r.costIn,
      "تكلفة الصادر": r.costOut,
    }));
    data.push({
      "الصنف": "الإجمالي", "الوارد": totals.totalIn, "الصادر": totals.totalOut, "الصافي": totals.net,
      "تكلفة الوارد": totals.costIn, "تكلفة الصادر": totals.costOut,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(k => ({ wch: k === "الصنف" ? 32 : 15 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "حركة الأصناف");
    XLSX.writeFile(wb, `StockMovementSummary_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setStockId("");
    setProduct(null);
    setMovement("");
    setSearch("");
  };

  const bName = branchName(branchId);
  const activeCount = (branchId ? 1 : 0) + (stockId ? 1 : 0) + (product ? 1 : 0) + (movement ? 1 : 0) + (search ? 1 : 0);

  return (
    <ReportPage
      title="حركة الأصناف (تجميعي)"
      subtitle="الكميات الواردة والصادرة وتكلفتها لكل صنف خلال الفترة"
      icon={<SwapVertIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "عدد الأصناف", value: fmtNumber(visibleRows.length), icon: <CategoryOutlinedIcon />, color: "primary" },
        { label: "إجمالي الوارد", value: fmtNumber(totals.totalIn), icon: <SouthWestIcon />, color: "success" },
        { label: "إجمالي الصادر", value: fmtNumber(totals.totalOut), icon: <NorthEastIcon />, color: "error" },
        { label: "صافي الحركة", value: fmtNumber(totals.net), icon: <CompareArrowsIcon />, color: "slate" },
        { label: "تكلفة الوارد", value: fmtMoney(totals.costIn), icon: <PaymentsOutlinedIcon />, color: "info" },
        { label: "تكلفة الصادر", value: fmtMoney(totals.costOut), icon: <AccountBalanceWalletIcon />, color: "warning", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="اسم الصنف..."
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={activeCount}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المستودع" value={stockId} onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          <ReportSelect
            label="نوع الحركة" value={movement} onChange={setMovement}
            options={[
              { value: "in", label: "له وارد" },
              { value: "out", label: "له صادر" },
              { value: "neg", label: "صافي بالسالب" },
            ]}
          />
          <Autocomplete
            size="small"
            options={products}
            value={product}
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => productIdOf(a) === productIdOf(b)}
            noOptionsText="لا توجد نتائج"
            renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="حركة الأصناف" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          localeText={{
            noRowsLabel: "لا توجد حركة في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}